import React, { useState, useEffect, useRef } from "react";
import { ChatCompletionRequest } from "../../../model_gen/chat";
import { TestSetService } from "../../../services/TestSetService";
import { usePermissions } from "../../../PermissionsProvider";
import { Card, CardContent } from "../../ui/card";
import TestSetEligibleRequestsTable from "../TestSetEligibleRequestsTable";

interface TestSetRequestsProps {
  onChange: (requests: ChatCompletionRequest[]) => void;
  intentGroupId: number;
  initialSelectedRequests?: ChatCompletionRequest[];
}

const TestSetRequests: React.FC<TestSetRequestsProps> = ({
  onChange,
  intentGroupId,
  initialSelectedRequests = [],
}) => {
  const { apiKey } = usePermissions();

  // State for eligible requests data fetching
  const [eligibleRequests, setEligibleRequests] = useState<ChatCompletionRequest[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalEligible, setTotalEligible] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialLoadDate] = useState<number>(Date.now());
  const eligibleRequestsCacheRef = useRef<{ [page: number]: ChatCompletionRequest[]; }>({});

  // Fetch eligible requests
  useEffect(() => {
    if (intentGroupId) {
      fetchEligibleRequests(currentPage);
    }
  }, [currentPage, intentGroupId, pageSize]);

  const fetchEligibleRequests = (page: number) => {
    if (eligibleRequestsCacheRef.current[page]) {
      setEligibleRequests(eligibleRequestsCacheRef.current[page]);
      return;
    }

    const offset = (page - 1) * pageSize;
    setIsLoading(true);

    // For new test sets, use -1 as testSetId
    TestSetService.getEligibleTestSetRequests(
      intentGroupId,
      -1, // No existing test set
      initialLoadDate,
      offset,
      pageSize,
      null,
      setIsLoading,
      apiKey
    ).subscribe(
      (data: any) => {
        const items = data.items || [];
        const totalCount = data.total || 0;

        setEligibleRequests(items);
        setTotalEligible(totalCount);
        eligibleRequestsCacheRef.current = {
          ...eligibleRequestsCacheRef.current,
          [page]: items,
        };
        setIsLoading(false);
      },
      (error: any) => {
        console.error(error);
        setIsLoading(false);
      }
    );
  };

  const handleRequestUpdate = (updatedRequest: ChatCompletionRequest) => {
    // Update the request in the selected requests if it exists
    const updatedRequests = initialSelectedRequests.map(request =>
      request.request_id === updatedRequest.request_id ? updatedRequest : request
    );
    
    // If the request wasn't in selected requests, add it
    if (!initialSelectedRequests.some(r => r.request_id === updatedRequest.request_id)) {
      updatedRequests.push(updatedRequest);
    }
    
    onChange(updatedRequests);

    // Also update the eligible requests if it exists there
    setEligibleRequests(prev =>
      prev.map(request =>
        request.request_id === updatedRequest.request_id ? updatedRequest : request
      )
    );

    // Update cache
    if (eligibleRequestsCacheRef.current[currentPage]) {
      eligibleRequestsCacheRef.current[currentPage] =
        eligibleRequestsCacheRef.current[currentPage].map(request =>
          request.request_id === updatedRequest.request_id ? updatedRequest : request
        );
    }
  };

  const totalPages = Math.ceil(totalEligible / pageSize);

  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div className="space-y-2">
        <h3 className="text-xl font-gc-med text-foreground">Select Requests</h3>
        <p className="text-sm text-muted-foreground">
          Choose requests to include in your test set and add tags for organization
        </p>
      </div>

      {/* Selection Summary */}
      {initialSelectedRequests.length > 0 && (
        <Card className="border-border bg-maitai-green/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm">
              <span className="font-gc-med text-foreground">
                {initialSelectedRequests.length} request{initialSelectedRequests.length !== 1 ? "s" : ""} selected
              </span>
              <span className="text-muted-foreground">for test set</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Requests Table */}
      <TestSetEligibleRequestsTable
        requests={eligibleRequests}
        loading={isLoading}
        apiKey={apiKey}
        selectedRequests={initialSelectedRequests}
        onSelectionChange={onChange}
        onRequestUpdate={handleRequestUpdate}
        selectable={true}
        showOnlySelected={false}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalEligible}
        itemsPerPage={pageSize}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default TestSetRequests;
